import { actualValueForDropdown } from '../../utils/common';

export const MAX_VISIBLE_PAV_IMAGES = 2;
export const PAV_ATTACHMENT_DRAG_TYPE = 'application/x-pav-attachment';

export const createId = (prefix = 'pav') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
export const createAttachmentId = (file, index) => `${createId('image')}-${index}-${file.name}-${file.size}`;

export const normalizeOcrKey = (key) => String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
export const normalizeOcrValue = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
export const formatOcrKey = (key) => String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const unwrapOcrValue = (field) => {
    if (field === undefined || field === null) return undefined;
    if (typeof field !== 'object') return field;
    if (field.value !== undefined && field.value !== null) return unwrapOcrValue(field.value);
    return field.id || field.title || field.label || field.name;
};

const getOcrAssetTypeSource = (ocrData) => (
    ocrData?.asset_type_id
    ?? ocrData?.asset_type
    ?? ocrData?.fields?.asset_type_id
    ?? ocrData?.fields?.asset_type
);

export const getOcrAssetTypeValue = (ocrData) => unwrapOcrValue(getOcrAssetTypeSource(ocrData));

export const getOcrAssetTypeSearchValue = (ocrData) => {
    const source = getOcrAssetTypeSource(ocrData);
    if (!source || typeof source !== 'object') return source;
    return source.title || source.label || source.name || unwrapOcrValue(source);
};

export const getOcrAssetTypeOption = (ocrData) => {
    const source = getOcrAssetTypeSource(ocrData);
    if (!source || typeof source !== 'object') return null;
    const assetType = source.value && typeof source.value === 'object' ? source.value : source;
    const id = assetType.id || assetType.asset_type_id || assetType.value;
    const label = assetType.title || assetType.label || assetType.name;
    const fields = assetType.additional_fields || assetType.fields;
    if (!id || !label || !Array.isArray(fields)) return null;
    return { id, key: id, value: id, label, fields, assetType };
};

const ASSET_TYPE_FIELD_KEYS = ['asset_type', 'asset_type_id'];

// Asset type may arrive as a full catalogue object; read its human name so it
// matches and groups against the plain-text values other images produce.
const unwrapOcrLabel = (field) => {
    if (!field || typeof field !== 'object') return field;
    if (field.value && typeof field.value === 'object') return unwrapOcrLabel(field.value);
    return field.title || field.label || field.name || unwrapOcrValue(field);
};

export const getAttachmentOcrFields = (attachment) => {
    const fields = Object.entries(attachment?.ocr_data?.fields || {})
        .map(([key, field]) => {
            const normalizedKey = normalizeOcrKey(key);
            const value = ASSET_TYPE_FIELD_KEYS.includes(normalizedKey) ? unwrapOcrLabel(field) : unwrapOcrValue(field);
            return {
                key,
                normalizedKey,
                value,
                normalizedValue: normalizeOcrValue(value),
                confidence: Number(field?.confidence || 0),
            };
        })
        .filter(({ normalizedKey, normalizedValue }) => normalizedKey && normalizedValue);
    const hasAssetTypeField = fields.some(({ normalizedKey }) => ASSET_TYPE_FIELD_KEYS.includes(normalizedKey));
    const assetTypeValue = getOcrAssetTypeSearchValue(attachment?.ocr_data) || getOcrAssetTypeValue(attachment?.ocr_data);
    if (!hasAssetTypeField && assetTypeValue) {
        fields.push({
            key: 'asset_type',
            normalizedKey: 'asset_type',
            value: assetTypeValue,
            normalizedValue: normalizeOcrValue(assetTypeValue),
            confidence: 1,
        });
    }
    return fields;
};

export const PAV_PENDING_ATTACHMENT_STATUSES = ['queued', 'uploading', 'extracting'];

// Everything a single image contributed, so a value that was prefilled wrongly
// can be traced back to the photo it was read from and corrected on the card.
export const getAttachmentOcrDetails = (attachment) => {
    const fields = getAttachmentOcrFields(attachment).map(({ key, value, confidence }) => ({
        key,
        label: formatOcrKey(key),
        value,
        confidence,
    }));
    if (PAV_PENDING_ATTACHMENT_STATUSES.includes(attachment?.status)) return { state: 'reading', fields, error: '' };
    if (attachment?.status === 'error') return { state: 'failed', fields, error: attachment?.error || '' };
    return { state: fields.length > 0 ? 'read' : 'empty', fields, error: '' };
};

const getAssetTypeCandidates = (option) => [
    option?.id,
    option?.key,
    option?.value,
    option?.label,
    option?.assetType?.id,
    option?.assetType?.title,
];

export const findAssetTypeOption = (options, ocrAssetTypeValue) => {
    const normalizedOcrValue = normalizeOcrValue(ocrAssetTypeValue);
    if (!normalizedOcrValue) return null;
    return (options || []).find((option) => (
        getAssetTypeCandidates(option).some((candidate) => normalizeOcrValue(candidate) === normalizedOcrValue)
    )) || null;
};

// Collapses everything that is not a letter or digit so "Router/Switch",
// "router - switch" and "RouterSwitch" all compare equal.
const toComparableAssetType = (value) => normalizeOcrValue(value).replace(/[^a-z0-9]+/g, '');
const toAssetTypeTokens = (value) => normalizeOcrValue(value).split(/[^a-z0-9]+/).filter(Boolean);

// OCR rarely returns a catalogue name verbatim — it reads "HP EliteBook 840 G6"
// or "Laptop Computer" where the catalogue holds "Laptop". Exact match is tried
// first, then punctuation-insensitive equality, then the longest catalogue name
// whose words all appear in the OCR text (longest wins so "Network Switch" beats
// a bare "Switch"). Returns null rather than guessing when nothing is close, so
// the card falls back to asking the user.
export const matchAssetTypeOption = (options, ocrAssetTypeValue) => {
    const exactMatch = findAssetTypeOption(options, ocrAssetTypeValue);
    if (exactMatch) return exactMatch;

    const comparableOcrValue = toComparableAssetType(ocrAssetTypeValue);
    if (!comparableOcrValue) return null;

    const comparableMatch = (options || []).find((option) => (
        getAssetTypeCandidates(option).some((candidate) => {
            const comparableCandidate = toComparableAssetType(candidate);
            return comparableCandidate && comparableCandidate === comparableOcrValue;
        })
    ));
    if (comparableMatch) return comparableMatch;

    const ocrTokens = new Set(toAssetTypeTokens(ocrAssetTypeValue));
    if (ocrTokens.size === 0) return null;

    let bestOption = null;
    let bestScore = 0;
    (options || []).forEach((option) => {
        const labelTokens = toAssetTypeTokens(option?.label || option?.assetType?.title);
        if (labelTokens.length === 0) return;
        // Every word of the catalogue name must appear in the OCR text.
        if (!labelTokens.every((token) => ocrTokens.has(token))) return;
        const score = labelTokens.join('').length;
        if (score > bestScore) {
            bestScore = score;
            bestOption = option;
        }
    });
    return bestOption;
};

export const reconcileOcrAttachments = (attachments) => {
    const canonicalFields = new Map();
    const conflictsByAttachmentId = new Map();

    // Upload order is the stable baseline even when OCR responses finish out of order.
    attachments.forEach((attachment) => {
        if (attachment.status !== 'complete' || !attachment.ocr_data) return;

        const fields = getAttachmentOcrFields(attachment);
        const conflicts = fields.reduce((result, field) => {
            const expected = canonicalFields.get(field.normalizedKey);
            if (expected && expected.normalizedValue !== field.normalizedValue) {
                result.push({ key: field.key, expected: expected.value, received: field.value });
            }
            return result;
        }, []);

        if (conflicts.length > 0) {
            conflictsByAttachmentId.set(attachment.id, conflicts);
            return;
        }

        fields.forEach((field) => {
            const current = canonicalFields.get(field.normalizedKey);
            if (!current || field.confidence > current.confidence) canonicalFields.set(field.normalizedKey, field);
        });
    });

    return { canonicalFields, conflictsByAttachmentId };
};

// Groups uploaded images into distinct assets purely from their OCR fields.
// Placement rules, in order: exact serial-number match wins; otherwise the most
// recently created group without any field conflict (photos of one asset arrive
// together); a conflict with every group means a different asset -> new group.
export const groupAttachmentsIntoAssets = (attachments) => {
    const groups = [];
    const groupByAttachmentId = new Map();

    attachments.forEach((attachment) => {
        if (attachment.status !== 'complete' || !attachment.ocr_data) return;
        const fields = getAttachmentOcrFields(attachment);
        if (fields.length === 0) return;

        const conflictsWith = (group) => fields.some((field) => {
            const existing = group.canonicalFields.get(field.normalizedKey);
            return existing && existing.normalizedValue !== field.normalizedValue;
        });

        const serialField = fields.find((field) => field.normalizedKey === 'serial_number');
        let target = serialField
            ? groups.find((group) => (
                group.canonicalFields.get('serial_number')?.normalizedValue === serialField.normalizedValue && !conflictsWith(group)
            ))
            : null;
        if (!target) {
            for (let index = groups.length - 1; index >= 0; index -= 1) {
                if (!conflictsWith(groups[index])) {
                    target = groups[index];
                    break;
                }
            }
        }
        if (!target) {
            target = { id: attachment.id, attachments: [], canonicalFields: new Map() };
            groups.push(target);
        }

        target.attachments.push(attachment);
        groupByAttachmentId.set(attachment.id, target);
        fields.forEach((field) => {
            const current = target.canonicalFields.get(field.normalizedKey);
            if (!current || field.confidence > current.confidence) target.canonicalFields.set(field.normalizedKey, field);
        });
    });

    // Unreadable images almost always sit next to readable shots of the same
    // asset in upload order, so they adopt the nearest preceding group.
    const orphans = [];
    let lastGroup = null;
    attachments.forEach((attachment) => {
        const assignedGroup = groupByAttachmentId.get(attachment.id);
        if (assignedGroup) {
            lastGroup = assignedGroup;
            return;
        }
        const isUnreadable = attachment.status === 'error'
            || (attachment.status === 'complete' && getAttachmentOcrFields(attachment).length === 0);
        if (!isUnreadable) return;
        if (lastGroup) {
            lastGroup.attachments.push(attachment);
            groupByAttachmentId.set(attachment.id, lastGroup);
        } else {
            orphans.push(attachment);
        }
    });
    if (orphans.length > 0) {
        if (groups.length > 0) {
            groups[0].attachments.unshift(...orphans);
            orphans.forEach((attachment) => groupByAttachmentId.set(attachment.id, groups[0]));
        } else {
            const orphanGroup = { id: orphans[0].id, attachments: orphans, canonicalFields: new Map() };
            groups.push(orphanGroup);
            orphans.forEach((attachment) => groupByAttachmentId.set(attachment.id, orphanGroup));
        }
    }

    return { groups, groupByAttachmentId };
};

export const buildCanonicalFields = (attachments) => {
    const canonicalFields = new Map();
    attachments.forEach((attachment) => {
        if (attachment.status !== 'complete' || !attachment.ocr_data) return;
        getAttachmentOcrFields(attachment).forEach((field) => {
            const current = canonicalFields.get(field.normalizedKey);
            if (!current || field.confidence > current.confidence) canonicalFields.set(field.normalizedKey, field);
        });
    });
    return canonicalFields;
};

// Applies the user's drag-and-drop corrections on top of the derived OCR
// grouping. `moves` maps attachment id -> card id (detected group or manual
// card). An attachment that is still uploading belongs to no group yet, so it is
// pulled straight from `attachments` and shown on its target card immediately.
// Moves naming an unknown attachment or card are ignored, so a stale move can
// never lose an image. Detected groups emptied by moves disappear; manual cards
// stay so they can still receive drops.
export const applyAttachmentMoves = ({ groups, manualCardIds, moves, attachments = [] }) => {
    const detectedGroups = groups.map((group) => ({ ...group, attachments: [...group.attachments] }));
    const manualGroups = manualCardIds.map((cardId) => ({ id: cardId, attachments: [], canonicalFields: new Map() }));
    const allCards = [...detectedGroups, ...manualGroups];
    const cardsById = new Map(allCards.map((card) => [card.id, card]));
    const attachmentsById = new Map(attachments.map((attachment) => [attachment.id, attachment]));

    let movedCount = 0;
    Object.entries(moves || {}).forEach(([attachmentId, targetCardId]) => {
        const target = cardsById.get(targetCardId);
        if (!target) return;
        const source = allCards.find((card) => card.attachments.some((item) => item.id === attachmentId));
        if (source?.id === target.id) return;
        const attachment = source
            ? source.attachments.find((item) => item.id === attachmentId)
            : attachmentsById.get(attachmentId);
        if (!attachment) return;
        if (source) source.attachments = source.attachments.filter((item) => item.id !== attachmentId);
        target.attachments.push(attachment);
        movedCount += 1;
    });

    if (movedCount > 0) {
        allCards.forEach((card) => {
            card.attachments.sort((left, right) => (
                attachments.indexOf(left) - attachments.indexOf(right)
            ));
            card.canonicalFields = buildCanonicalFields(card.attachments);
        });
    }

    return {
        detectedGroups: detectedGroups.filter((group) => group.attachments.length > 0),
        manualGroups,
    };
};

const serializeAttachments = (attachments) => attachments
    .filter((attachment) => attachment.url)
    .map(({ preview_url, file, status, error, ...attachment }) => attachment);

export const getPavAssetFieldType = (field) => String(field?.type || field?.input_type || 'TEXT').trim().toUpperCase();

const unwrapPavAssetFieldValue = (value) => {
    if (!value || typeof value !== 'object') return value;
    return value.value ?? value.id ?? value.key ?? value.label;
};

export const serializePavAssetFieldValue = (field, value) => {
    if (getPavAssetFieldType(field) === 'MULTI_SELECT') {
        return (Array.isArray(value) ? value : []).map(unwrapPavAssetFieldValue).map((item) => typeof item === 'string' ? item.trim() : item);
    }
    const serializedValue = unwrapPavAssetFieldValue(value) ?? '';
    return typeof serializedValue === 'string' ? serializedValue.trim() : serializedValue;
};

export const hasPavAssetFieldValue = (field, value) => {
    const serializedValue = serializePavAssetFieldValue(field, value);
    if (Array.isArray(serializedValue)) return serializedValue.length > 0;
    return serializedValue !== undefined && serializedValue !== null && String(serializedValue).trim() !== '';
};

// A PAV physical verification asks whether the asset is functional, so a
// `NEW` working condition is shown as `WORKING` on screen. This is a label swap
// only: `NEW` stays the stored value and is what gets serialized back to the API.
const PAV_WORKING_CONDITION_LABELS = { NEW: 'WORKING' };

export const getPavWorkingConditionLabel = (key, value) => {
    if (normalizeOcrKey(key) !== 'working_condition') return value;
    return PAV_WORKING_CONDITION_LABELS[String(value ?? '').trim().toUpperCase()] ?? value;
};

export const getPavAssetFieldDisplayValue = (field, value) => {
    if (getPavAssetFieldType(field) === 'MULTI_SELECT') {
        return (Array.isArray(value) ? value : [])
            .map((item) => item?.label ?? unwrapPavAssetFieldValue(item))
            .filter((item) => item !== undefined && item !== null && String(item).trim())
            .join(', ');
    }
    return getPavWorkingConditionLabel(field?.key, value?.label ?? unwrapPavAssetFieldValue(value) ?? '');
};

export const getMediaViewAttachments = (attachments) => attachments
    .map((attachment, index) => {
        const url = attachment.url || attachment.file_url || attachment.preview_url;
        const name = attachment.name || attachment.file_name || `Asset image ${index + 1}`;
        return {
            ...attachment,
            id: attachment.id || `${url}-${index}`,
            url,
            name,
            fileName: name,
            mime_type: attachment.mime_type || attachment.file?.type || 'image/jpeg',
        };
    })
    .filter((attachment) => attachment.url);

export const buildAssetData = (values, attachments) => ({
    owner_name: values.owner_name,
    contact_email: values.contact_email,
    asset_type_id: actualValueForDropdown(values.asset_type_id),
    attachments: serializeAttachments(attachments),
    additional_fields: (values.asset_type_id?.fields || []).map((field) => ({
        key: field.key,
        value: serializePavAssetFieldValue(field, values[field.key]),
    })),
});

const getAssetFieldValue = (asset, key) => (
    (asset.additional_fields || []).find((field) => normalizeOcrKey(field.key) === normalizeOcrKey(key))?.value || ''
);

const selectPavAssetOcrData = (attachments = []) => {
    const candidates = attachments
        .filter((attachment) => attachment?.ocr_data)
        .map((attachment) => {
            const fields = getAttachmentOcrFields(attachment);
            const hasAssetType = Boolean(
                getOcrAssetTypeSearchValue(attachment.ocr_data)
                || getOcrAssetTypeValue(attachment.ocr_data)
                || fields.some(({ normalizedKey }) => ASSET_TYPE_FIELD_KEYS.includes(normalizedKey))
            );
            const hasSerialNumber = Boolean(
                normalizeOcrValue(attachment.ocr_data?.serial_number)
                || fields.some(({ normalizedKey }) => normalizedKey === 'serial_number')
            );
            return { data: attachment.ocr_data, hasAssetType, hasSerialNumber };
        });

    return (
        candidates.find(({ hasAssetType, hasSerialNumber }) => hasAssetType && hasSerialNumber)
        || candidates.find(({ hasAssetType }) => hasAssetType)
        || candidates.find(({ hasSerialNumber }) => hasSerialNumber)
        || candidates[0]
    )?.data;
};

// Owner details are optional. Only the values that were actually entered are
// sent, and the `owner` key is dropped entirely when none of them exist.
export const buildAssetOwner = (asset) => {
    const name = String(asset?.owner_name || '').trim();
    const email = String(asset?.contact_email || '').trim();
    if (!name && !email) return undefined;
    return {
        ...(name ? { name } : {}),
        ...(email ? { email } : {}),
    };
};

export const buildPavAssetPayload = ({ asset, ticketId, milestoneId }) => {
    const ocrData = selectPavAssetOcrData(asset.attachments);
    const owner = buildAssetOwner(asset);
    return {
        asset_type_id: asset.asset_type_id,
        milestone_id: milestoneId,
        ticket_id: ticketId,
        serial_number: getAssetFieldValue(asset, 'serial_number'),
        ...(ocrData ? { ocr_data: ocrData } : {}),
        additional_fields: (asset.additional_fields || []).map(({ key, value }) => ({
            key,
            value: typeof value === 'string' ? value.trim() : value,
        })),
        attachments: (asset.attachments || [])
            .map((attachment) => ({
                name: attachment.name,
                file_url: attachment.file_url || attachment.url,
                mime_type: attachment.mime_type,
            }))
            .filter((attachment) => attachment.file_url),
        ...(owner ? { owner } : {}),
    };
};

const getSavedAssetValue = (asset, key) => (
    asset?.[key]
    ?? asset?.asset?.[key]
    ?? asset?.metadata?.[key]
);

// Fields saved through a dropdown can come back as `{ value, label }` instead of
// a plain string, so the readable value is unwrapped before it is displayed.
const readPlainValue = (value) => (
    value && typeof value === 'object' && !Array.isArray(value)
        ? (value.value ?? value.label ?? value.title ?? '')
        : value
);
const getSavedEntityId = (entity) => entity && typeof entity === 'object' ? entity.id : entity;

export const getSavedAssetTypeLabel = (asset) => (
    asset?.asset_type?.title
    || asset?.assetType?.title
    || asset?.asset?.asset_type?.title
    || asset?.asset?.assetType?.title
    || asset?.asset_type_name
    || asset?.title
    || 'Asset'
);

export const getSavedAssetTypeId = (asset) => {
    const assetTypeId = getSavedAssetValue(asset, 'asset_type_id')
        || asset?.asset_type?.id
        || asset?.assetType?.id
        || asset?.asset?.asset_type?.id
        || asset?.asset?.assetType?.id;
    if (assetTypeId && typeof assetTypeId === 'object') {
        return assetTypeId.id || assetTypeId.value || assetTypeId.key;
    }
    return assetTypeId;
};

export const getSavedAssetSerialNumber = (asset) => (
    getSavedAssetValue(asset, 'serial_number')
    || getSavedAssetValue(asset, 'serial_no')
    || ''
);

export const getSavedAssetIdentity = (asset, index = '') => {
    const serialNumber = getSavedAssetSerialNumber(asset);
    const rawAssetTypeId = getSavedAssetValue(asset, 'asset_type_id')
        || asset?.asset_type?.id
        || asset?.assetType?.id
        || asset?.asset?.asset_type?.id
        || asset?.asset?.assetType?.id;
    const assetTypeId = rawAssetTypeId && typeof rawAssetTypeId === 'object'
        ? rawAssetTypeId.id
        : rawAssetTypeId || getSavedAssetTypeLabel(asset);
    if (serialNumber) return `serial:${normalizeOcrValue(serialNumber)}:${normalizeOcrValue(assetTypeId)}`;

    const rawId = asset?.id
        || asset?.asset_id
        || asset?.inventory_ledger_id
        || asset?.fk_asset_id
        || asset?.asset?.id
        || asset?.__pavLocalId;
    const id = rawId && typeof rawId === 'object' ? rawId.id : rawId;
    return id ? `id:${id}` : `asset:${index}`;
};

// A saved asset can arrive from the create response, the ticket payload and the
// refreshed ticket detail, and those shapes do not always produce the same
// identity string. Matching on the API asset ID and then the ledger ID first
// keeps a refreshed entry replacing its older copy instead of being appended
// beside it, so state that only the refresh carries — the physical verification
// flags among them — actually reaches the UI.
const isSameSavedAsset = (left, leftIndex, right, rightIndex) => {
    const leftAssetId = getSavedAssetApiId(left);
    const rightAssetId = getSavedAssetApiId(right);
    if (leftAssetId && rightAssetId) return String(leftAssetId) === String(rightAssetId);

    const leftLedgerId = getSavedAssetLedgerId(left);
    const rightLedgerId = getSavedAssetLedgerId(right);
    if (leftLedgerId && rightLedgerId) return String(leftLedgerId) === String(rightLedgerId);

    return getSavedAssetIdentity(left, leftIndex) === getSavedAssetIdentity(right, rightIndex);
};

export const mergeSavedAssets = (currentAssets, incomingAssets) => {
    const merged = [...currentAssets];
    incomingAssets.forEach((asset, incomingIndex) => {
        const existingIndex = merged.findIndex((current, currentIndex) => isSameSavedAsset(current, currentIndex, asset, incomingIndex));
        if (existingIndex >= 0) merged[existingIndex] = { ...merged[existingIndex], ...asset };
        else merged.push(asset);
    });
    return merged;
};

export const getSavedAssetOwner = (asset) => ({
    name: asset?.owner?.name || asset?.asset_owner?.name || getSavedAssetValue(asset, 'owner_name') || '',
    email: asset?.owner?.email || asset?.asset_owner?.email || getSavedAssetValue(asset, 'owner_email') || getSavedAssetValue(asset, 'contact_email') || '',
    mobile: asset?.owner?.mobile || asset?.asset_owner?.mobile || getSavedAssetValue(asset, 'owner_mobile') || getSavedAssetValue(asset, 'contact_mobile') || '',
});

export const getSavedAssetApiId = (asset) => (
    getSavedEntityId(asset?.asset?.id)
    || getSavedEntityId(asset?.asset_id)
    || getSavedEntityId(asset?.fk_asset_id)
    || getSavedEntityId(asset?.id)
);

export const getSavedAssetLedgerId = (asset) => (
    getSavedEntityId(asset?.ledger_id)
    || getSavedEntityId(asset?.inventory_ledger_id)
    || getSavedEntityId(asset?.inventory_ledger?.id)
    || (String(getSavedEntityId(asset?.id) || '').startsWith('inventoryledger_') ? getSavedEntityId(asset?.id) : undefined)
);

export const getSavedAssetFields = (asset) => {
    const additionalFields = asset?.additional_fields || asset?.asset?.additional_fields || [];
    const normalizedAdditionalFields = Array.isArray(additionalFields)
        ? additionalFields.map((field) => ({
            key: field?.key || field?.name || field?.label,
            label: field?.label || formatOcrKey(field?.key || field?.name),
            value: field?.value,
        }))
        : Object.entries(additionalFields).map(([key, value]) => ({ key, label: formatOcrKey(key), value }));
    const directFields = [
        ['serial_number', 'Serial Number'],
        ['partcode', 'Partcode'],
        ['make', 'Make'],
        ['model', 'Model'],
        ['working_condition', 'Working Condition'],
    ].map(([key, label]) => ({ key, label, value: readPlainValue(getSavedAssetValue(asset, key)) }));
    const seenKeys = new Set();

    return [...directFields, ...normalizedAdditionalFields].filter((field) => {
        const normalizedKey = normalizeOcrKey(field.key);
        const hasValue = field.value !== undefined && field.value !== null && String(field.value).trim() !== '';
        if (!normalizedKey || !hasValue || seenKeys.has(normalizedKey)) return false;
        seenKeys.add(normalizedKey);
        return true;
    });
};

export const getSavedAssetAttachments = (asset) => {
    const attachments = asset?.medias
        || asset?.asset?.medias
        || asset?.attachments
        || asset?.asset?.attachments
        || asset?.asset_files
        || [];
    return Array.isArray(attachments) ? attachments.filter((attachment) => attachment?.file_url || attachment?.url) : [];
};

// The added-assets list arrives from the API in one response, so its search box
// filters in memory. It matches on the serial number alone - the one value that
// identifies an asset unambiguously - so this is precomputed once per asset
// rather than on every keystroke.
export const getSavedAssetSerialSearchText = (asset) => String(getSavedAssetSerialNumber(asset) || '')
    .trim()
    .toLowerCase();

// A partial serial is enough to find a row; an empty query keeps the list whole.
export const matchesSavedAssetSerial = (serialText, query) => {
    const term = String(query || '').trim().toLowerCase();
    if (!term) return true;
    return serialText.includes(term);
};
